import {
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Spinner } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAuthStore } from "src/store/authStore";
import { useLogout } from "src/hooks/useAuth";
import { getReportsApi } from "src/api/reportApi";
import type { HeaderReportsResponse } from "src/types/header";
import type { DashboardListResponse } from "src/types/dashboard";
import { getDashboardsApi, setDashboardDefaultApi } from "src/api/dashboardApi";
import useDashboardStore from "src/store/dashboardStore";
import {SiteLogo} from "../../assets";

type ToggleButtonProps = {
  children: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

const UserMenuToggle = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ children, className, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onClick?.(event);
      }}
    >
      {children}
    </button>
  ),
);

UserMenuToggle.displayName = "UserMenuToggle";

export default function AuthHeader() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);

  const { pathname } = useLocation();
  console.log(pathname);

  const currentDashboardCodeFromPath = pathname.startsWith("/dashboards/")
    ? decodeURIComponent(pathname.replace("/dashboards/", ""))
    : "";

  const navigate = useNavigate();
  const { user: userData, refreshToken } = useAuthStore();
  const { defaultDashboard, setDefaultDashboard, resetDashboardStore } =
    useDashboardStore();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const queryClient = useQueryClient();

  const {
    mutate: setDefault,
    isPending: isSettingDefault,
    variables: settingDefaultCode,
  } = useMutation({
    mutationFn: (code: string) => setDashboardDefaultApi(code),
    onMutate: async (code) => {
      await queryClient.cancelQueries({ queryKey: ["dashboards"] });
      const previous = queryClient.getQueryData<DashboardListResponse>([
        "dashboards",
      ]);
      queryClient.setQueryData<DashboardListResponse>(["dashboards"], (old) => {
        if (!old) return old;
        return {
          ...old,
          dashboards: old.dashboards.map((d) => ({
            ...d,
            is_default: d.code === code,
          })),
        };
      });
      setDefaultDashboard(code);
      return { previous };
    },
    onSuccess: () => {
      toast.success("Dashboard set as default.");
    },
    onError: (error: Error, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["dashboards"], context.previous);
        const prevDefault = context.previous.dashboards.find(
          (d) => d.is_default,
        );
        setDefaultDashboard(prevDefault?.code ?? "");
      }
      toast.error(error.message || "Failed to set dashboard as default.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });

  const builderPath = currentDashboardCodeFromPath
    ? `/builder/${currentDashboardCodeFromPath}`
    : "/builder";

  // const { data: allReports } = useQuery<HeaderReportsResponse, Error>({
  //   queryKey: ["reports"],
  //   queryFn: () => getReportsApi<HeaderReportsResponse>(),
  //   enabled: !!userData,
  //   // Never serve stale data — always hit the backend on every page/sort change
  //   staleTime: 0,
  //   // Remove from cache immediately so navigating back always refetches
  //   gcTime: 0,
  // });

  const { data: allDashboards } = useQuery<DashboardListResponse, Error>({
    queryKey: ["dashboards"],
    queryFn: () => getDashboardsApi(),
    enabled: !!userData,
  });

  useEffect(() => {
    const foundDashboard = allDashboards?.dashboards?.find(
      (dash) => dash.is_default === true,
    );

    if (!defaultDashboard)
      if (foundDashboard) setDefaultDashboard(foundDashboard.code);
      else setDefaultDashboard(allDashboards?.dashboards?.[0]?.code ?? "");

    return () => {
      if (defaultDashboard) setDefaultDashboard("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDashboards, defaultDashboard]);

  return (
    <nav className="navbar navbar-expand-lg auth-header py-0 z-2">
      <div className="container-fluid px-5">
        <Link
          className="navbar-brand text-decoration-none my-1 me-0 py-2 pe-3"
          to={"/dashboards"}
        >
          <img src={SiteLogo} alt="Site logo" width={150} />
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#authHeader"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse ms-3" id="authHeader">
          <ul className="navbar-nav me-auto dashboard-tabs">
            {!allDashboards ? (
              <div className="placeholder-glow nav-item">
                <span
                  className="placeholder"
                  style={{ width: "160px", height: "30px" }}
                ></span>
              </div>
            ) : (
              allDashboards?.dashboards?.map((dashboard) => {
                const isActive =
                  currentDashboardCodeFromPath === dashboard?.code;
                return (
                  <li key={dashboard?.code} className="nav-item">
                    <button
                      type="button"
                      className={`nav-link dashboard-tab${isActive ? " custom-active-link" : ""}`}
                      onClick={() => navigate(`/dashboards/${dashboard?.code}`)}
                    >
                      {dashboard?.is_default ? (
                        <i className="bi bi-star-fill dashboard-default-star me-2" />
                      ) : (
                        <span
                          role="button"
                          className="set-default-btn me-2"
                          title="Set as default"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              isSettingDefault &&
                              settingDefaultCode === dashboard?.code
                            )
                              return;
                            setDefault(dashboard.code);
                          }}
                        >
                          {isSettingDefault &&
                          settingDefaultCode === dashboard?.code ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            <i className="bi bi-star" />
                          )}
                        </span>
                      )}
                      <span className="dashboard-tab__label">
                        {dashboard?.name}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
            {/* {!allReports ? (
              <div className="placeholder-glow nav-item">
                <span
                  className="placeholder"
                  style={{ width: "160px", height: "30px" }}
                ></span>
              </div>
            ) : (
              <Dropdown
                as="li"
                className="nav-item"
                show={isReportDropdownOpen}
                onToggle={(nextShow) => setIsReportDropdownOpen(nextShow)}
              >
                <Dropdown.Toggle
                  id="user-dropdown"
                  as={UserMenuToggle}
                  className={`nav-link ${
                    isReportDropdownOpen || pathname.includes("report")
                      ? "custom-active-link"
                      : ""
                  }`}
                >
                  Reports
                </Dropdown.Toggle>
                <Dropdown.Menu
                  popperConfig={{
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, 12] },
                      },
                    ],
                  }}
                >
                  {allReports?.reports
                    // ?.filter((report) => report?.user_types?.includes(userData?.ref_type || ""))
                    ?.map((report) => (
                      <Dropdown.Item
                        key={report?.code}
                        as="button"
                        onClick={() => navigate(`/reports/${report?.code}`)}
                      >
                        {report?.name}
                      </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            )} */}
          </ul>

          <ul className="navbar-nav justify-content-end navbar-end">
            {!userData ? (
              <div className="placeholder-glow">
                <span
                  className="placeholder"
                  style={{ width: "160px", height: "30px" }}
                ></span>
              </div>
            ) : (
              <Dropdown
                as="li"
                className="nav-item"
                show={isUserMenuOpen}
                onToggle={(nextShow) => setIsUserMenuOpen(nextShow)}
              >
                <Dropdown.Toggle
                  id="user-dropdown"
                  as={UserMenuToggle}
                  className={`nav-link ${
                    isUserMenuOpen ? "custom-active-link" : ""
                  }`}
                >
                  {[userData?.firstname, userData?.lastname]
                    .filter(Boolean)
                    .join(" ") || userData?.name}
                </Dropdown.Toggle>

                <Dropdown.Menu
                  popperConfig={{
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, 12] },
                      },
                    ],
                  }}
                >
                  <Dropdown.Item
                    as={Link}
                    to={builderPath}
                    onClick={() => resetDashboardStore()}
                  >
                    Dashboard Builder
                  </Dropdown.Item>
                  {userData?.admin && (
                    <Dropdown.Item as={Link} to="/user-management">
                      User Management
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item
                    as="button"
                    onClick={() => logout(refreshToken || "")}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />{" "}
                        Signing out…
                      </>
                    ) : (
                      "Logout"
                    )}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
}
